import React from 'react';
import { ORANGE_ANALYTICS, WHITE, YELLOW_ANALYTICS } from '../../utils/colours';
import { BoundingBox } from './AnalyticsOverlay.styles';
import TrajectoryOverlay from './TrajectoryOverlay';
import {
    AnalyticsBoxCoordinates,
    AnalyticsOverlayMarker,
    AnalyticsOverlayShape,
    AnalyticsOverlayTrack,
    Coordinate,
} from '../../types/analytics';
import { LineCrossingDirection } from '@manything/types-notifications';
import useResizeObserver from '../../hooks/useResizeObserver';
import { AnalyticsLabel } from '@manything/types-notifications';

interface ImagePosition {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface DirectionArrow {
    from: Coordinate;
    tip: Coordinate;
    head: Coordinate[];
}

const EMPTY_STYLE: React.CSSProperties = {};
const TRACKS_CONTAINER_STYLE: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    transition: 'opacity 1.2s ease-in-out',
};

const getDirectionArrows = (
    shapeSearch: AnalyticsOverlayShape,
    aspectRatio: number = 1,
    arrowHeadScale: number = 1
): DirectionArrow[] => {
    if (
        shapeSearch.type !== 'line' ||
        shapeSearch.coordinates.length < 2 ||
        shapeSearch.direction == null
    ) {
        return [];
    }

    const scaleX = aspectRatio > 0 ? aspectRatio : 1;
    // Scale X into display-space units so perpendicular vectors remain visually perpendicular
    // on non-square thumbnails, then convert back to normalized coordinates for rendering.
    const scaleForAspectRatio = ([x, y]: Coordinate): Coordinate => [x * scaleX, y];
    const unscaleForAspectRatio = ([x, y]: Coordinate): Coordinate => [x / scaleX, y];

    const [start, end] = shapeSearch.coordinates.map(scaleForAspectRatio);
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const magnitude = Math.hypot(dx, dy);

    if (!magnitude) {
        return [];
    }

    const midpoint: Coordinate = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
    ];
    const unitDirectionX = dx / magnitude;
    const unitDirectionY = dy / magnitude;
    const arrowLength = 0.08 + 0.032 * (arrowHeadScale - 1);
    const arrowHeadLength = 0.032 * arrowHeadScale;
    const arrowHeadWidth = 0.04 * arrowHeadScale;
    const sides =
        shapeSearch.direction === LineCrossingDirection.Both
            ? [true, false]
            : [shapeSearch.direction === LineCrossingDirection.Negative];

    return sides.map((positiveSide) => {
        const normalX = -unitDirectionY * (positiveSide ? 1 : -1);
        const normalY = unitDirectionX * (positiveSide ? 1 : -1);
        const tip: Coordinate = [
            midpoint[0] + normalX * arrowLength,
            midpoint[1] + normalY * arrowLength,
        ];
        const headBase: Coordinate = [
            tip[0] - normalX * arrowHeadLength,
            tip[1] - normalY * arrowHeadLength,
        ];
        const perpendicularX = -normalY;
        const perpendicularY = normalX;

        return {
            from: unscaleForAspectRatio(midpoint),
            tip: unscaleForAspectRatio(tip),
            head: [
                unscaleForAspectRatio(tip),
                unscaleForAspectRatio([
                    headBase[0] + (perpendicularX * arrowHeadWidth) / 2,
                    headBase[1] + (perpendicularY * arrowHeadWidth) / 2,
                ]),
                unscaleForAspectRatio([
                    headBase[0] - (perpendicularX * arrowHeadWidth) / 2,
                    headBase[1] - (perpendicularY * arrowHeadWidth) / 2,
                ]),
            ],
        };
    });
};

interface ShapeSearchOverlayProps {
    aspectRatio: number;
    shapeSearch: AnalyticsOverlayShape;
    style?: React.CSSProperties;
    directionArrowHeadScale?: number;
    imagePosition?: ImagePosition;
}

const ShapeSearchOverlay = React.memo(
    ({
        aspectRatio,
        shapeSearch,
        style = EMPTY_STYLE,
        directionArrowHeadScale = 1,
        imagePosition,
    }: ShapeSearchOverlayProps) => {
        const directionArrows = getDirectionArrows(
            shapeSearch,
            aspectRatio,
            directionArrowHeadScale
        );
        const coordinates = shapeSearch.coordinates;
        const points = coordinates.map(([x, y]) => `${x},${y}`).join(' ');

        return (
            <div
                style={{
                    position: 'absolute',
                    ...(imagePosition
                        ? {
                              left: `${imagePosition.left}px`,
                              top: `${imagePosition.top}px`,
                              width: `${imagePosition.width}px`,
                              height: `${imagePosition.height}px`,
                          }
                        : {
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                          }),
                    pointerEvents: 'none',
                    zIndex: 1,
                    transition: 'opacity 1.2s ease-in-out',
                    ...style,
                }}
            >
                <svg
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                    }}
                    viewBox="0 0 1 1"
                    preserveAspectRatio="none"
                >
                    {shapeSearch.type === 'line' ? (
                        <>
                            <polyline
                                points={points}
                                fill="none"
                                stroke={YELLOW_ANALYTICS}
                                strokeWidth="2"
                                opacity="1"
                                vectorEffect="non-scaling-stroke"
                            />
                            {coordinates.map(([x, y], idx) => (
                                <circle
                                    key={`point-${idx}`}
                                    cx={x}
                                    cy={y}
                                    r="0.01"
                                    fill={WHITE()}
                                    opacity="0.75"
                                />
                            ))}
                            {directionArrows.map((arrow, idx) => (
                                <React.Fragment key={`direction-arrow-${idx}`}>
                                    <line
                                        x1={arrow.from[0]}
                                        y1={arrow.from[1]}
                                        x2={arrow.tip[0]}
                                        y2={arrow.tip[1]}
                                        stroke={YELLOW_ANALYTICS}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    <polygon
                                        points={arrow.head
                                            .map(([x, y]) => `${x},${y}`)
                                            .join(' ')}
                                        fill={YELLOW_ANALYTICS}
                                    />
                                </React.Fragment>
                            ))}
                        </>
                    ) : (
                        <>
                            <polygon
                                points={points}
                                fill={YELLOW_ANALYTICS}
                                fillOpacity="0.2"
                                stroke={YELLOW_ANALYTICS}
                                strokeWidth="2"
                                opacity="1"
                                vectorEffect="non-scaling-stroke"
                            />
                            {coordinates.map(([x, y], idx) => (
                                <circle
                                    key={`point-${idx}`}
                                    cx={x}
                                    cy={y}
                                    r="0.01"
                                    fill={WHITE()}
                                    opacity="0.3"
                                />
                            ))}
                        </>
                    )}
                </svg>
            </div>
        );
    }
);

ShapeSearchOverlay.displayName = 'ShapeSearchOverlay';

interface AnalyticsOverlayProps {
    /** Array of smart motion or normalised alert-history tracks to draw with the shared overlay style */
    tracks?: Array<AnalyticsOverlayTrack | AnalyticsOverlayMarker>;
    /** When passing in Marker[] then label is required because it wouldn't be part of every track */
    label?: AnalyticsLabel;
    /** Optional position/dimensions for the overlay container */
    imagePosition?: ImagePosition;
    /** Additional styles for the container */
    style?: React.CSSProperties;
    /** Additional styles for rendered detection tracks without affecting shape-search geometry */
    tracksStyle?: React.CSSProperties;
    /** Whether to render detection track bounding boxes */
    showTrackBoundingBoxes?: boolean;
    /** Unique identifier for the overlay */
    id?: string;
    /** Optional shape search/rule geometry to render on overlay */
    shapeSearch?: AnalyticsOverlayShape | null;
    /** Disable SVG filters for performance testing on iOS devices */
    disableFilters?: boolean;
    /** Scale factor for line-crossing arrowheads */
    directionArrowHeadScale?: number;
    /** Known aspect ratio for fixed-size overlays so no resize observer is needed */
    fixedAspectRatio?: number;
}

const AnalyticsOverlayDynamic: React.FC<AnalyticsOverlayProps> = ({
    tracks,
    label,
    imagePosition,
    style = EMPTY_STYLE,
    tracksStyle = EMPTY_STYLE,
    showTrackBoundingBoxes = true,
    id,
    shapeSearch,
    disableFilters = true,
    directionArrowHeadScale = 1,
    fixedAspectRatio,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [aspectRatio, setAspectRatio] = React.useState(() =>
        fixedAspectRatio ??
        (imagePosition?.width && imagePosition?.height
            ? imagePosition.width / imagePosition.height
            : 1)
    );

    useResizeObserver(containerRef, () => {
        const { width, height } =
            containerRef.current?.getBoundingClientRect() ?? {};

        if (!width || !height) {
            return;
        }

        const nextAspectRatio = width / height;
        setAspectRatio((previousAspectRatio) =>
            Math.abs(previousAspectRatio - nextAspectRatio) < 0.001
                ? previousAspectRatio
                : nextAspectRatio
        );
    }, fixedAspectRatio == null);

    React.useEffect(() => {
        if (fixedAspectRatio != null) {
            setAspectRatio(fixedAspectRatio);
            return;
        }

        if (!imagePosition?.width || !imagePosition?.height) {
            return;
        }

        setAspectRatio(imagePosition.width / imagePosition.height);
    }, [fixedAspectRatio, imagePosition?.height, imagePosition?.width]);

    const hasTracks = Boolean(tracks && tracks.length > 0);

    if (!hasTracks && !shapeSearch) {
        return null;
    }

    const getTrackColour = (trackLabel?: AnalyticsLabel | string): string => {
        const effectiveLabel = trackLabel ?? label;
        return effectiveLabel === 'person' ? YELLOW_ANALYTICS : ORANGE_ANALYTICS;
    };

    const getTrackOffset = (track: AnalyticsOverlayTrack | AnalyticsOverlayMarker): { x: number; y: number } => {
        const trackLabel = 'label' in track ? track.label : label;
        if (trackLabel !== 'person') {
            return { x: 0, y: 0};
        }

        let coords: AnalyticsBoxCoordinates | undefined;
        if (tracks && tracks.length > 1 && 'groupCoords' in track && track.groupCoords) {
            coords = track.groupCoords;
        } else if ('heroThumbCoords' in track && track.heroThumbCoords) {
            coords = track.heroThumbCoords;
        } else if ('thumbCoords' in track && track.thumbCoords) {
            coords = track.thumbCoords;
        }
        
        if (!coords) {
            return { x: 0, y: 0 };
        }

        const height = coords[3] - coords[1];
        return {
            x: 0,
            y: Math.round(height / 6)
        };
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                ...(imagePosition ? {
                    left: `${imagePosition.left}px`,
                    top: `${imagePosition.top}px`,
                    width: `${imagePosition.width}px`,
                    height: `${imagePosition.height}px`
                } : {
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                }),
                pointerEvents: 'none',
                zIndex: 1,
                transition: 'opacity 1.2s ease-in-out',
                ...style
            }}
        >
            {shapeSearch && (
                <ShapeSearchOverlay
                    shapeSearch={shapeSearch}
                    aspectRatio={aspectRatio}
                    directionArrowHeadScale={directionArrowHeadScale}
                />
            )}

            {hasTracks && (
                <div style={{ ...TRACKS_CONTAINER_STYLE, ...tracksStyle }}>
                    {/* bounding boxes */}
                    {showTrackBoundingBoxes && tracks?.map((track, idx) => {
                        const trackLabel = 'label' in track ? track.label : label;
                        
                        // Generate unique ID: use detectionId+instanceId for Track[], or passed id prop for Marker[]
                        let uniqueId: string;
                        if ('detectionId' in track && track.detectionId) {
                            const instanceSuffix = 'instanceId' in track && track.instanceId ? `_${track.instanceId}` : '';
                            uniqueId = `${track.detectionId}${instanceSuffix}`;
                        } else {
                            uniqueId = id ? `${id}_${idx}` : `${idx}`;
                        }
                        
                        let coords: AnalyticsBoxCoordinates | undefined;
                        if (tracks && tracks.length > 1 && 'groupCoords' in track && track.groupCoords) {
                            coords = track.groupCoords;
                        } else if ('heroThumbCoords' in track && track.heroThumbCoords) {
                            coords = track.heroThumbCoords;
                        } else if ('thumbCoords' in track && track.thumbCoords) {
                            coords = track.thumbCoords;
                        }
                        
                        if (!coords) {
                            return null;
                        }

                        return (
                            <BoundingBox 
                                key={`bbox_${trackLabel}_${uniqueId}`}
                                $coords={coords}
                                $colour={getTrackColour(trackLabel)}
                            />
                        );
                    })}

                    {/* trajectory paths */}
                    {tracks?.map((track, idx) => {
                        const trackLabel = 'label' in track ? track.label : label;
                        
                        // Generate unique ID: use detectionId+instanceId for Track[], or passed id prop for Marker[]
                        let uniqueId: string;
                        if ('detectionId' in track && track.detectionId) {
                            const instanceSuffix = 'instanceId' in track && track.instanceId ? `_${track.instanceId}` : '';
                            uniqueId = `${track.detectionId}${instanceSuffix}`;
                        } else {
                            uniqueId = id ? `${id}_${idx}` : `${idx}`;
                        }
                        
                        if (!track.allCoords || track.allCoords.length === 0) {
                            return null;
                        }

                        return (
                            <TrajectoryOverlay
                                key={`trajectory_${trackLabel}_${uniqueId}`}
                                id={`trajectory_${trackLabel}_${uniqueId}`}
                                allCoords={track.allCoords}
                                colour={getTrackColour(trackLabel)}
                                offset={getTrackOffset(track)}
                                disableFilters={disableFilters}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const AnalyticsOverlay: React.FC<AnalyticsOverlayProps> = (props) => {
    const {
        tracks,
        shapeSearch,
        fixedAspectRatio,
        imagePosition,
        style = EMPTY_STYLE,
        directionArrowHeadScale = 1,
    } = props;

    if (
        shapeSearch &&
        (!tracks || tracks.length === 0) &&
        fixedAspectRatio != null
    ) {
        return (
            <ShapeSearchOverlay
                shapeSearch={shapeSearch}
                aspectRatio={fixedAspectRatio}
                directionArrowHeadScale={directionArrowHeadScale}
                imagePosition={imagePosition}
                style={style}
            />
        );
    }

    return <AnalyticsOverlayDynamic {...props} />;
};

export default React.memo(AnalyticsOverlay);
